"use client";

import { LocaleLink, useLocalePathname } from "@i18n/routing";
import { config } from "@repo/config";
import { useLoginModal } from "@saas/auth/components/LoginModalProvider";
import { useSession } from "@saas/auth/hooks/use-session";
import { UserMenu } from "@saas/shared/components/UserMenu";
import { LocaleSwitch } from "@shared/components/LocaleSwitch";
import { Logo } from "@shared/components/Logo";
import { useFullscreenMenu } from "@shared/hooks/use-fullscreen-menu";
import { Button } from "@ui/components/button";
import {
	NavigationMenu,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
} from "@ui/components/navigation-menu";
import { cn } from "@ui/lib";
import { ChevronDownIcon, MenuIcon, XIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";
import { NAV_DROPDOWNS, isNavHrefActive } from "../lib/site-nav";

/** 顶部导航链接：DeeVid 原版是 Playfair 衬线，放到我们全站 Inter/Poppins 的页面上显得突兀，
 *  2026-09-30 老公拍板改回无衬线，和 hero 输入卡同一套 Inter */
/** 手机全屏菜单（对标 imgveo）：一行一项，48px 高，字号比桌面导航大一档 */
const MOBILE_MENU_ID = "mobile-nav-menu";
const MOBILE_ROW_CLASS =
	"flex min-h-12 w-full items-center justify-between font-medium text-lg";

const NAV_LINK_CLASS =
	"flex h-10 items-center rounded-2xl px-3 font-medium font-ui text-[15px] transition-colors hover:bg-gray-3";

export function NavBar() {
	const t = useTranslations();
	const { user } = useSession();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const localePathname = useLocalePathname();
	const [isTop, setIsTop] = useState(true);
	const [isClient, setIsClient] = useState(false);
	const [isNavVisible, setIsNavVisible] = useState(true);
	const [lastScrollY, setLastScrollY] = useState(0);

	// Always call useLoginModal unconditionally
	const loginModal = useLoginModal();

	// Then use its result conditionally
	const showLoginModal =
		isClient && loginModal ? loginModal.showLoginModal : () => {};
	// 原地弹登录框，登录后留在当前页（含 locale 前缀和 query），
	// 和首页 Create 按钮派发 show-login 时传的路径口径一致
	const openLogin = () =>
		showLoginModal(window.location.pathname + window.location.search);

	useEffect(() => {
		setIsClient(true);
	}, []);

	// Mobile navbar auto-hide on scroll down, show on scroll up
	useEffect(() => {
		const handleScrollDirection = () => {
			const currentScrollY = window.scrollY;
			const isMobile = window.innerWidth < 1024;

			if (isMobile) {
				// Only trigger if scrolled more than 5px to prevent flickering
				if (currentScrollY > lastScrollY && currentScrollY > 60) {
					// Scrolling down - hide navbar
					setIsNavVisible(false);
				} else if (currentScrollY < lastScrollY) {
					// Scrolling up - show navbar
					setIsNavVisible(true);
				}
			} else {
				// Always visible on desktop
				setIsNavVisible(true);
			}

			setLastScrollY(currentScrollY);
		};

		window.addEventListener("scroll", handleScrollDirection, {
			passive: true,
		});
		return () =>
			window.removeEventListener("scroll", handleScrollDirection);
	}, [lastScrollY]);

	const debouncedScrollHandler = useDebounceCallback(
		() => {
			setIsTop(window.scrollY <= 10);
		},
		150,
		{
			maxWait: 150,
		},
	);

	useEffect(() => {
		window.addEventListener("scroll", debouncedScrollHandler);
		debouncedScrollHandler();
		return () => {
			window.removeEventListener("scroll", debouncedScrollHandler);
		};
	}, [debouncedScrollHandler]);

	useEffect(() => {
		setMobileMenuOpen(false);
	}, [localePathname]);

	// 点当前页的链接路径不变，上面的 pathname effect 不会关，链接上自己关
	const closeMobileMenu = () => setMobileMenuOpen(false);

	// 和汉堡的 lg:hidden 同一个断点
	useFullscreenMenu(mobileMenuOpen, closeMobileMenu, "(min-width: 64rem)");

	const isDocsPage = localePathname.startsWith("/docs");

	const isMenuItemActive = (href: string) =>
		isNavHrefActive(localePathname, href);

	return (
		<>
			<nav
				className={cn(
					"fixed left-0 z-50 w-full font-poppins transition-all duration-300 ease-in-out",
					// 滚动后不用死白：半透明白 + 毛玻璃吃下面内容的颜色（对标 imgveo），
					// 压在淡紫 hero 上是淡紫、压在白/灰区块上是白/灰；只留一条极淡发丝线，不加阴影
					!isTop || isDocsPage || mobileMenuOpen
						? "border-black/[.05] border-b bg-white/70 backdrop-blur-xl"
						: "border-transparent border-b shadow-none",
					// Mobile auto-hide behavior
					!isNavVisible ? "-translate-y-full" : "translate-y-0",
					"lg:translate-y-0", // Always visible on desktop
				)}
				style={{ top: "var(--language-banner-height, 0px)" }}
				data-test="navigation"
			>
				<div className="container">
					<div
						className={cn(
							"flex items-center justify-stretch gap-6 transition-[padding] duration-200",
							// 对标 imgveo：顶部 64px、滚动后 60px，别做厚
							!isTop || isDocsPage ? "py-2.5" : "py-3",
							// 手机（汉堡模式）固定 55 + nav 底线 1 = 56，和工作台手机页头一样高（10/21）
							"max-lg:h-[55px] max-lg:py-0",
						)}
					>
						<div className="flex flex-1 justify-start">
							<LocaleLink
								href="/"
								className="block hover:no-underline active:no-underline"
							>
								<Logo smOnMobile />
							</LocaleLink>
						</div>

						<div className="hidden flex-1 items-center justify-center lg:flex">
							<NavigationMenu className="relative">
								<NavigationMenuList className="gap-2">
									<NavigationMenuItem>
										<NavigationMenuLink
											asChild
											className={cn(
												NAV_LINK_CLASS,
												isMenuItemActive("/")
													? "text-brand"
													: "text-gray-12",
											)}
										>
											<LocaleLink href="/">
												{t("common.menu.home")}
											</LocaleLink>
										</NavigationMenuLink>
									</NavigationMenuItem>

									{/* 对标 imgveo：两个下拉、两列、单色线条图标，当前页整组标蓝 */}
									{NAV_DROPDOWNS.map((group) => (
										<NavigationMenuItem
											key={group.labelKey}
										>
											<NavigationMenuTrigger
												className={cn(
													NAV_LINK_CLASS,
													// 覆盖 shadcn 默认的 accent（青绿）悬停/展开底色
													"bg-transparent focus:bg-gray-3 data-[state=open]:bg-gray-3",
													group.items.some((item) =>
														isMenuItemActive(
															item.href,
														),
													)
														? "text-brand hover:text-brand focus:text-brand"
														: "text-gray-12 hover:text-gray-12 focus:text-gray-12",
												)}
											>
												{t(group.labelKey)}
											</NavigationMenuTrigger>
											<NavigationMenuContent>
												<div className="grid w-[680px] grid-cols-2 gap-1 p-3">
													{group.items.map((item) => (
														<NavigationMenuLink
															key={item.href}
															asChild
														>
															<LocaleLink
																href={item.href}
																className={cn(
																	"flex items-center gap-3 rounded-xl px-3 py-2.5 font-ui text-sm no-underline outline-none transition-colors hover:bg-gray-3 focus:bg-gray-3",
																	isMenuItemActive(
																		item.href,
																	)
																		? "bg-gray-3 text-brand"
																		: "text-gray-12",
																)}
															>
																<item.icon className="size-4 shrink-0 opacity-70" />
																{t(
																	item.titleKey,
																)}
															</LocaleLink>
														</NavigationMenuLink>
													))}
												</div>
											</NavigationMenuContent>
										</NavigationMenuItem>
									))}

									<NavigationMenuItem>
										<NavigationMenuLink
											asChild
											className={cn(
												NAV_LINK_CLASS,
												isMenuItemActive("/pricing")
													? "text-brand"
													: "text-gray-12",
											)}
										>
											<LocaleLink href="/pricing">
												{t("common.menu.pricing")}
											</LocaleLink>
										</NavigationMenuLink>
									</NavigationMenuItem>

									<NavigationMenuItem>
										<NavigationMenuLink
											asChild
											className={cn(
												NAV_LINK_CLASS,
												isMenuItemActive("/blog")
													? "text-brand"
													: "text-gray-12",
											)}
										>
											<LocaleLink href="/blog">
												{t("common.menu.blog")}
											</LocaleLink>
										</NavigationMenuLink>
									</NavigationMenuItem>
								</NavigationMenuList>
							</NavigationMenu>
						</div>

						{/* 桌面端收到 gap-2：语言按钮是 44px 点击区、图标只占 16px，gap-3 看着和
						    登录按钮离得太远；gap-1 悬停底色又贴住登录按钮。手机端旁边是汉堡，保持 gap-3 */}
						<div className="flex flex-1 items-center justify-end gap-3 lg:gap-2">
							{/* 桌面端排到最右（登录按钮右边）；手机端不动，汉堡要固定在最右 */}
							{config.i18n.enabled && (
								<div className="lg:order-last">
									<LocaleSwitch />
								</div>
							)}

							{/* 头像 / 登录钮都在汉堡左边：手机上汉堡固定最右，登录前后同一个位置 */}
							{config.ui.saas.enabled && user && (
								<UserMenu mobileSheet />
							)}

							{config.ui.saas.enabled && !user && (
								<Button
									key="login"
									className={cn(
										// 手机上照 AIReel 只放文字（10/25），桌面才是胶囊
										"h-11 shrink-0 whitespace-nowrap rounded-full border-0 bg-transparent px-2 font-semibold text-brand text-sm hover:bg-transparent hover:text-brand lg:px-5 lg:shadow-card",
										// 滚动后顶栏是半透明白，白胶囊只剩一圈淡阴影、看着像纯文字，换浅灰底
										!isTop || isDocsPage
											? "lg:bg-gray-4 lg:hover:bg-gray-5"
											: "lg:bg-white lg:hover:bg-gray-2",
									)}
									variant="ghost"
									onClick={openLogin}
								>
									{t("common.menu.login")}
								</Button>
							)}

							<Button
								className="lg:hidden -mr-2 min-w-[44px] min-h-[44px]"
								size="icon"
								variant="ghost"
								aria-label="Menu"
								aria-expanded={mobileMenuOpen}
								aria-controls={MOBILE_MENU_ID}
								onClick={() =>
									setMobileMenuOpen((open) => !open)
								}
							>
								{mobileMenuOpen ? (
									<XIcon className="size-5" />
								) : (
									<MenuIcon className="size-4" />
								)}
							</Button>
						</div>
					</div>
				</div>
			</nav>

			{/* 手机全屏菜单：铺在 navbar 下面，navbar（logo、头像、×）一直露着，× 就在汉堡原位；
			    z-45 压过同为 z-40 的吸底 CTA（MobileStickyCta） */}
			{mobileMenuOpen && (
				<div
					id={MOBILE_MENU_ID}
					className="fixed inset-x-0 bottom-0 z-45 overflow-y-auto bg-background lg:hidden"
					style={{ top: "var(--language-banner-height, 0px)" }}
				>
					<div className="container flex flex-col pt-[63px] pb-10">
						<LocaleLink
							onClick={closeMobileMenu}
							href="/"
							className={cn(
								MOBILE_ROW_CLASS,
								isMenuItemActive("/")
									? "font-semibold text-foreground"
									: "text-foreground/70",
							)}
						>
							{t("common.menu.home")}
						</LocaleLink>
						{/* 登录后给一个回作品库的入口（竞品叫 Studio），营销页上原本找不到 */}
						{user && (
							<LocaleLink
								onClick={closeMobileMenu}
								href="/my-assets"
								className={cn(
									MOBILE_ROW_CLASS,
									"text-foreground/70",
								)}
							>
								{t("app.userMenu.myAssets")}
							</LocaleLink>
						)}
						{/* 和桌面端同两组，原生 details 折叠，当前页所在组默认展开 */}
						{NAV_DROPDOWNS.map((group) => (
							<details
								key={group.labelKey}
								className="group"
								open={group.items.some((item) =>
									isMenuItemActive(item.href),
								)}
							>
								<summary
									className={cn(
										MOBILE_ROW_CLASS,
										"cursor-pointer list-none text-foreground/70 [&::-webkit-details-marker]:hidden",
									)}
								>
									{t(group.labelKey)}
									<ChevronDownIcon className="size-5 transition-transform group-open:rotate-180" />
								</summary>
								<div className="flex flex-col pb-2 pl-4">
									{group.items.map((item) => (
										<LocaleLink
											onClick={closeMobileMenu}
											key={item.href}
											href={item.href}
											className={cn(
												"flex min-h-11 items-center gap-3 text-base",
												isMenuItemActive(item.href)
													? "font-semibold text-brand"
													: "text-foreground/70",
											)}
										>
											<item.icon className="size-4 opacity-70" />
											{t(item.titleKey)}
										</LocaleLink>
									))}
								</div>
							</details>
						))}
						{(["/pricing", "/blog"] as const).map((href) => (
							<LocaleLink
								onClick={closeMobileMenu}
								key={href}
								href={href}
								className={cn(
									MOBILE_ROW_CLASS,
									isMenuItemActive(href)
										? "font-semibold text-foreground"
										: "text-foreground/70",
								)}
							>
								{t(
									href === "/pricing"
										? "common.menu.pricing"
										: "common.menu.blog",
								)}
							</LocaleLink>
						))}
						{/* 未登录：顶栏已有登录文字钮，这里再给实心主按钮 */}
						{!user && (
							<Button
								className="mt-6 h-12 w-full rounded-full border-0 bg-brand font-semibold text-base text-white hover:bg-brand-500"
								onClick={() => {
									setMobileMenuOpen(false);
									openLogin();
								}}
							>
								{t("app.userMenu.startForFree")}
							</Button>
						)}
					</div>
				</div>
			)}
		</>
	);
}
